import type { NextRequest } from 'next/server';
import type { PageViewEvent } from '../types';

interface EdgeAnalyticsConfig {
  projectId: string;
  apiEndpoint?: string;
  sendInDevelopment?: boolean;
}

/**
 * Track page view from edge runtime (middleware)
 */
export async function trackPageViewEdge(
  request: NextRequest,
  config: EdgeAnalyticsConfig
): Promise<void> {
  const { 
    projectId, 
    apiEndpoint = 'https://api.cosmic.new/analytics',
    sendInDevelopment = false 
  } = config;
  
  if (!projectId) {
    console.warn('Cosmic Analytics Edge: projectId is required');
    return;
  }
  
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Skip analytics in development unless explicitly enabled
  if (isDevelopment && !sendInDevelopment) {
    console.log('Cosmic Analytics Edge: Disabled in development mode. Set sendInDevelopment: true to enable.');
    return;
  }
  
  const url = request.url;
  const userAgent = request.headers.get('user-agent') || '';
  const referrer = request.headers.get('referer') || '';
  const acceptLanguage = request.headers.get('accept-language') || '';
  
  // Extract primary language
  const language = acceptLanguage.split(',')[0]?.split(';')[0] || 'en';
  
  // Generate session ID from cookies or create new one
  const sessionCookie = request.cookies.get('cosmic_session_id');
  const sessionId = sessionCookie?.value || `edge-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  
  const event: PageViewEvent = {
    event: 'pageview',
    projectId,
    sessionId,
    url,
    title: '', // Title not available in edge runtime
    referrer: referrer || 'direct',
    timestamp: Date.now(),
    language,
    userAgent,
  };
  
  try {
    await fetch(`${apiEndpoint}/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Forwarded-For': request.headers.get('x-forwarded-for') || request.headers.get('x-real-ip') || '',
        'X-Real-IP': request.headers.get('x-real-ip') || '',
      },
      body: JSON.stringify({ events: [event] }),
    });
  } catch (error) {
    console.error('Cosmic Analytics Edge: Failed to track page view', error);
  }
}

/**
 * Extract geo information from request headers
 */
export function getGeoInfo(request: NextRequest): {
  country?: string;
  region?: string;
  city?: string;
} {
  return {
    country: request.headers.get('x-vercel-ip-country') || request.headers.get('cf-ipcountry') || undefined,
    region: request.headers.get('x-vercel-ip-country-region') || request.headers.get('cf-region') || undefined,
    city: request.headers.get('x-vercel-ip-city') || request.headers.get('cf-city') || undefined,
  };
} 